/**
 * reader.js — a PDF page as the two things table detection needs: positioned
 * text and drawn lines.
 *
 * pdf.js hands text over readily and lines not at all: they live in the
 * operator list as path-construction commands with a transform that has to be
 * tracked by hand. Skipping them is tempting and wrong — a bordered table
 * states its own column geometry exactly, and inferring what is already written
 * down is how a tool gets a bordered invoice wrong.
 *
 * WHY THE VIEWPORT IS CHOSEN, NOT TAKEN. `/Rotate` is metadata: a page can
 * carry the wrong value, none at all while its content lies sideways, or one a
 * previous tool already baked in. Trusting it makes the row grouper read a
 * column of sideways glyphs as one enormous row, silently. So each of the four
 * right-angle viewports is scored by how much text runs left-to-right in it and
 * the winner is used — the same measurement /document-translate makes, for the
 * same reason.
 *
 * Exposed as window.P2XReader.
 */
(function (root) {
    'use strict';
    if (root.P2XReader) return;

    function uprightViewport(Util, page, items, scale) {
        var base = page.rotate || 0, best = null;
        for (var k = 0; k < 4; k++) {
            var rot = ((base + k * 90) % 360 + 360) % 360;
            var vp = page.getViewport({ scale: scale, rotation: rot });
            var score = 0;
            for (var i = 0; i < items.length; i++) {
                var tx = Util.transform(vp.transform, items[i].transform);
                var sx = Math.hypot(tx[0], tx[1]);
                if (!(sx > 0)) continue;
                score += (tx[0] / sx) * ((items[i].width || 0) + 1);
            }
            if (!best || score > best.score) best = { rot: rot, score: score, vp: vp };
        }
        return best || { rot: base, vp: page.getViewport({ scale: scale }) };
    }

    function itemBox(Util, vp, it) {
        var tx = Util.transform(vp.transform, it.transform);
        var sx = Math.hypot(tx[0], tx[1]);
        var h = Math.hypot(tx[2], tx[3]) || 1;
        var w = (it.width || 0) * vp.scale;
        if (!(w > 0) || !(h > 0) || !(sx > 0)) return null;
        var ux = tx[0] / sx, uy = tx[1] / sx, vx = tx[2] / h, vy = tx[3] / h;
        var xs = [], ys = [];
        for (var a = 0; a <= 1; a++) {
            for (var b = 0; b <= 1; b++) {
                xs.push(tx[4] + ux * w * a + vx * h * b);
                ys.push(tx[5] + uy * w * a + vy * h * b);
            }
        }
        return {
            text: it.str,
            x: Math.min.apply(null, xs), y: Math.min.apply(null, ys),
            w: Math.max.apply(null, xs) - Math.min.apply(null, xs),
            h: Math.max.apply(null, ys) - Math.min.apply(null, ys),
            angle: Math.atan2(uy, ux),
        };
    }

    function apply(m, x, y) { return [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]]; }

    /**
     * Drawn line segments, in the same device space as the text.
     *
     * Only the path commands are walked, and only their endpoints are kept: a
     * table border is a straight run, and everything curved is decoration. The
     * CTM is tracked through save/restore/transform exactly as a renderer would
     * — the ops carry coordinates in whatever space was current, and a rule
     * placed inside a `cm` block lands somewhere else entirely without it.
     */
    async function drawnLines(pdfjsLib, page, vp) {
        var OPS = pdfjsLib.OPS, Util = pdfjsLib.Util;
        var list;
        try { list = await page.getOperatorList(); } catch (e) { return []; }

        var base = vp.transform;
        var ctm = base.slice(), stack = [];
        var out = [];

        function push(x0, y0, x1, y1) {
            var a = apply(ctm, x0, y0), b = apply(ctm, x1, y1);
            var dx = Math.abs(a[0] - b[0]), dy = Math.abs(a[1] - b[1]);
            if (dx < 0.4 && dy < 0.4) return;               // a dot, not a line
            if (dx > 2 && dy > 2) return;                    // diagonal — decoration
            out.push({ x0: a[0], y0: a[1], x1: b[0], y1: b[1] });
        }

        for (var i = 0; i < list.fnArray.length; i++) {
            var fn = list.fnArray[i], args = list.argsArray[i];
            if (fn === OPS.save) { stack.push(ctm.slice()); continue; }
            if (fn === OPS.restore) { if (stack.length) ctm = stack.pop(); continue; }
            if (fn === OPS.transform) { ctm = Util.transform(ctm, args); continue; }

            if (fn === OPS.constructPath) {
                var ops = args[0], co = args[1];
                var p = 0, cx = 0, cy = 0, sx = 0, sy = 0;
                for (var o = 0; o < ops.length; o++) {
                    switch (ops[o]) {
                        case OPS.moveTo: cx = co[p++]; cy = co[p++]; sx = cx; sy = cy; break;
                        case OPS.lineTo: push(cx, cy, co[p], co[p + 1]); cx = co[p++]; cy = co[p++]; break;
                        case OPS.curveTo: p += 6; cx = co[p - 2]; cy = co[p - 1]; break;
                        case OPS.curveTo2: p += 4; cx = co[p - 2]; cy = co[p - 1]; break;
                        case OPS.curveTo3: p += 4; cx = co[p - 2]; cy = co[p - 1]; break;
                        case OPS.closePath: push(cx, cy, sx, sy); cx = sx; cy = sy; break;
                        case OPS.rectangle: {
                            // A table border is very often four thin rectangles
                            // rather than four strokes; each side is a line.
                            var rx = co[p++], ry = co[p++], rw = co[p++], rh = co[p++];
                            push(rx, ry, rx + rw, ry);
                            push(rx + rw, ry, rx + rw, ry + rh);
                            push(rx + rw, ry + rh, rx, ry + rh);
                            push(rx, ry + rh, rx, ry);
                            break;
                        }
                        default: break;
                    }
                }
            }
        }
        return out;
    }

    /**
     * readPage(pdfjsLib, page, scale) → { items, lines, width, height, rotation }
     */
    async function readPage(pdfjsLib, page, scale) {
        scale = scale || 1;
        var Util = pdfjsLib.Util;
        var content = await page.getTextContent();
        var real = content.items.filter(function (q) { return q.str && q.str.trim() && q.transform; });
        var up = uprightViewport(Util, page, real, scale);

        var items = [], skewed = 0;
        for (var i = 0; i < real.length; i++) {
            var b = itemBox(Util, up.vp, real[i]);
            if (!b) continue;
            // A run still at an angle after the page is squared up is a stamp or
            // a watermark; feeding it to the row grouper invents rows.
            if (Math.abs(b.angle) > 0.35) { skewed++; continue; }
            items.push(b);
        }

        return {
            items: items,
            lines: await drawnLines(pdfjsLib, page, up.vp),
            width: up.vp.width, height: up.vp.height,
            rotation: up.rot, skewed: skewed,
        };
    }

    root.P2XReader = {
        readPage: readPage,
        _uprightViewport: uprightViewport,
        _itemBox: itemBox,
        _drawnLines: drawnLines,
    };
})(typeof self !== 'undefined' ? self : this);
